/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { IALBServicesAssetDetails, LicenseTierType } from 'generated-types';
import { SchemaService } from 'ajs/modules/core/services/schema-service';
import { Subject } from 'rxjs';

import './license-tier-select-card.component.less';
import * as l10n from './license-tier-select-card.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *
 *     Provide selection functionality for a specific type of a license tier.
 *     This component doesn't touch any data-model. Selection is done by event binding.
 *
 * @author Zhiqian Liu
 */
@Component({
    selector: 'license-tier-select-card',
    templateUrl: './license-tier-select-card.component.html',
})
export class LicenseTierSelectCardComponent implements OnDestroy {
    /**
     * Type of the license tier.
     */
    @Input()
    public tierType: LicenseTierType;

    /**
     * Fraction string of tier usage, in the form of (used/total).
     */
    @Input()
    public tierUsageFraction!: string;

    /**
     * Currently in-use license tier type.
     */
    @Input()
    public tierInUse: LicenseTierType;

    /**
     * Decide whether to disable selection functionality of this card.
     */
    @Input()
    public disabled!: boolean;

    /**
     * Used to display contact email for cloud licensing.
     */
    @Input()
    public assetDetails?: IALBServicesAssetDetails;

    /**
     * Called when this card is selected.
     */
    @Output()
    public onSelect: EventEmitter<LicenseTierType> = new EventEmitter();

    public readonly l10nKeys = l10nKeys;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        @Inject(SchemaService)
        private readonly schemaService: SchemaService,
        private readonly l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Get email from asset details.
     */
    public get contactEmail(): string {
        return this.assetDetails?.email ?? '';
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Handler of clicking of the card. Trigger selection only when allowed.
     */
    public onCardClick(): void {
        if (!this.disabled) {
            this.onSelect.emit(this.tierType);
        }
    }

    /**
     * Get the label of a specific type of license tier.
     */
    public getTierTypeLabel(tierType: LicenseTierType): string {
        if (tierType === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
            return this.l10nService.getMessage(this.l10nKeys.cloudLicenses);
        } else if (tierType === LicenseTierType.ENTERPRISE) {
            return this.l10nService.getMessage(this.l10nKeys.onPremLicenses);
        }

        return this.schemaService.getEnumValue('LicenseTierType', tierType).label;
    }

    /**
     * Get the description of a specific type of license tier.
     */
    public getTierTypeDescription(tierType: LicenseTierType): string {
        if (tierType === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
            return this.l10nService.getMessage(this.l10nKeys.cloudLicensesDescription);
        } else if (tierType === LicenseTierType.ENTERPRISE) {
            return this.l10nService.getMessage(this.l10nKeys.onPremLicensesDescription);
        }

        return this.schemaService.getEnumValue('LicenseTierType', tierType).description;
    }
}
